/**
 * The Document tab: the one PDF this form produces.
 *
 * It was the fifth card on Settings — the most complex editor in the product,
 * below Uploads, reached by scrolling past the notification emails. And it was
 * shaped as a choice between three renderers ("a branded record" / "Smart PDF"
 * / "composed"), which is a question about our implementation rather than
 * about their document.
 *
 * So: its own tab, and two things a person can see instead of three modes —
 * optional TEMPLATES (your own PDFs, and blank pages to place answers on) and
 * an ordered list of PARTS. What gets
 * sent follows from those, and the server derives it (`sanitizeDocument`); the
 * word "mode" appears nowhere on this screen and is never sent from it.
 *
 * R5 (screen 13, docs/PLAYBOOK-redesign.md §7): the tab is laid out like the
 * builder — the Outline (with p1/p2 tags on placed fields) · the PDF page,
 * with the boxes dragged in place · the inspector. Pick a field in the
 * Outline and **Place** it; drag a node onto the page; or, on a tablet, tap a
 * chip and then the page. The generated-parts document has the same
 * inspector, and the rendered preview with sample answers is the middle
 * column when there is no PDF (and a Preview view when there is).
 *
 * The plan gate moved with it. It is on the UPLOAD and nothing else: Free can
 * arrange parts, send the branded record and name the file. See the file input
 * below, which is the only inert control on this page — and, on a lapsed
 * plan, the stored PDF's positions, which show but do not move.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConnectionRow, DocBlock, FormPdfTemplate, FormSettingsView, WorkflowGraph, WorkflowNode,
  addBlankTemplate, deleteFormTemplate, getFormBuilder, getFormParents, getFormWorkflow, listFormConnections, previewFormDocument,
  saveDraftDocument, templateForPart, templatesOf, uploadFormTemplate, workingDocumentOf,
} from '../../api/forms';
import type { FieldDefinition } from '../../shared/types';
import { asksNothing } from '../../shared/types';
import { draftKeyOf, liveKeyOf } from '../../shared/documentRename';
import { arrangePages, pagePartsOf } from '../../shared/documentPages';
import FieldPicker from './FieldPicker';
import { OptGroup, groupsForContainer, pickerGroups, sectionsFromLayout } from './fieldGroups';
import {
  Armed, Placement, PlacementCanvas, PlacementProps, PlacementSel, Region, ScopeOption, newPlacement, newRegion,
} from './PlacementEditor';
import DocBlockEditor, {
  DocBlockContext, DocBlockKind, KIND_LABEL, blockSummary, newBlock, palette, templateName,
} from './DocBlockEditor';
import type { FieldGroup } from './ConnectionEditors';
import { TokenPicker, describeGroup, useCaretInsert } from './ConnectionEditors';
import { Modal, RowMenu, SaveBar, SectionLabel } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { useDirty, useUnsavedGuard } from '../../components/unsaved';
import { useFormWorkspace } from './FormWorkspace';
import { pageThumbnails } from './pdfjs';
import BuilderOutline, { BuilderSel } from './BuilderOutline';
import type { BField, BSection } from './builderModel';
import { NARROW, PHONE, useMediaQuery } from '../../shared/media';
import { usePageHeight } from './workflow/usePageHeight';
import { ParentForm, parentFieldDefs, parentOptGroups } from './parentFields';

/** The lifetimes card 3 offers. `null` is "until the submission is deleted". */
const TTL_CHOICES: { value: string; days: number | null; label: string }[] = [
  { value: '7', days: 7, label: '7 days' },
  { value: '30', days: 30, label: '30 days' },
  { value: '90', days: 90, label: '90 days' },
  { value: '365', days: 365, label: '1 year' },
  { value: 'never', days: null, label: 'Until the submission is deleted' },
];
const ttlValueOf = (days: number | null | undefined): string => {
  if (days === null) return 'never';
  if (days === undefined) return '30';
  return TTL_CHOICES.find((c) => c.days === days)?.value || '30';
};

/** One line for the "Your own PDF is on every paid plan" upsell. */
function PaidLine() {
  return (
    <div className="hint">
      Your own PDF is on every paid plan · <Link to="/admin/settings/billing/plans">See the plans →</Link>
    </div>
  );
}

/** How many PDFs (blank pages included) a document may hold — the server's MAX_TEMPLATES. */
const MAX_TEMPLATES = 10;
/** How many pages a blank may have — the server's MAX_BLANK_PAGES. */
const MAX_BLANK_PAGES = 20;

/** What a save may change on one PDF: which answer fills which named field, and where the rest are placed. */
interface TemplateEdit { acroMap: Record<string, string>; placements: Placement[]; regions: Region[] }
const NO_EDIT: TemplateEdit = { acroMap: {}, placements: [], regions: [] };
const editOf = (t: FormPdfTemplate): TemplateEdit => ({
  acroMap: t.acroMap || {},
  placements: (t.placements as Placement[] | undefined) || [],
  regions: (t.regions as Region[] | undefined) || [],
});

/** "3 pages · 612×792pt · no named fields…", for one PDF. */
function factsOf(t: FormPdfTemplate, acroMap: Record<string, string>): string {
  if (t.blank) return `${t.pages} page${t.pages === 1 ? '' : 's'} · ${t.width}×${t.height}pt · empty — place answers on it`;
  return [
    `${t.pages} page${t.pages === 1 ? '' : 's'}`,
    `${t.width}×${t.height}pt`,
    t.acroFields?.length
      ? `${t.acroFields.length} named field${t.acroFields.length === 1 ? '' : 's'}, `
        + `${Object.keys(acroMap).length} matched — check the mapping`
      : 'no named fields, so every answer is positioned on the page',
  ].join(' · ');
}

/** Where a placed box starts when it is PLACED rather than dropped: an inch in, staggered down the page. */
const PLACE_X = 72;
const PLACE_TOP = 72;
const PLACE_STEP = 24;

/**
 * The tab keeps its own working state from the moment it opens. When the
 * draft's document is replaced from outside it — a discard, a version
 * restore, another session's draft — it starts again from what is stored.
 */
export default function FormDocument() {
  const { documentEpoch } = useFormWorkspace();
  return <FormDocumentTab key={documentEpoch} />;
}

function FormDocumentTab() {
  const { form, refreshForm, noteDocumentDraft, features, draft } = useFormWorkspace();
  // The DRAFT's document when it has one: this tab rides the draft, and what
  // is live is only what it started from.
  const d0 = workingDocumentOf(form) || {};
  const toast = useToast();

  /**
   * The PDFs — uploads and blank pages. Held in state rather than read from
   * `form` because adding, replacing and removing one act IMMEDIATELY — they
   * move bytes, so there is nothing for a Save to do — while everything
   * layered on top of each (`edits`) rides the save.
   */
  const [templates, setTemplates] = useState<FormPdfTemplate[]>(() => templatesOf(d0));
  const [edits, setEdits] = useState<Record<string, TemplateEdit>>(
    () => Object.fromEntries(templatesOf(d0).map((t) => [t.id as string, editOf(t)]))
  );
  /** The PDF on the canvas — the one the Place button, the placed list and the mapping are about. */
  const [activeId, setActiveId] = useState<string | undefined>(() => templatesOf(d0)[0]?.id);
  const template = templates.find((t) => t.id === activeId) || templates[0];
  const { acroMap, placements, regions } = (template && edits[template.id as string]) || NO_EDIT;
  const multi = templates.length > 1;
  const [uploading, setUploading] = useState(false);
  /** The rule-5 dialog before a Replace that would clear positions. */
  const [replacing, setReplacing] = useState<{ file: File; id: string } | null>(null);
  /** The same dialog before a blank loses pages that have answers on them. */
  const [shrinking, setShrinking] = useState<{ id: string; pages: number; lost: number } | null>(null);

  /** Edit the PDF on the canvas. */
  const patchActive = (fn: (e: TemplateEdit) => TemplateEdit) => {
    if (!template?.id) return;
    const id = template.id;
    setEdits((m) => ({ ...m, [id]: fn(m[id] || NO_EDIT) }));
  };
  type Next<T> = T | ((was: T) => T);
  const setPlacements = (v: Next<Placement[]>) =>
    patchActive((e) => ({ ...e, placements: typeof v === 'function' ? v(e.placements) : v }));
  const setRegions = (v: Next<Region[]>) =>
    patchActive((e) => ({ ...e, regions: typeof v === 'function' ? v(e.regions) : v }));
  const setAcroMap = (v: Next<Record<string, string>>) =>
    patchActive((e) => ({ ...e, acroMap: typeof v === 'function' ? v(e.acroMap) : v }));
  /** Every PDF with what is on screen for it — what a save and a preview send. */
  const withEdits = useMemo(
    () => templates.map((t) => ({ ...t, ...(edits[t.id as string] || editOf(t)) })),
    [templates, edits]
  );

  const [blocks, setBlocks] = useState<DocBlock[]>(d0.blocks || []);
  /** Which part's modal is open, by index. */
  const [editingPart, setEditingPart] = useState<number | null>(null);
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const [filename, setFilename] = useState(d0.filename || '');
  const [ttl, setTtl] = useState(ttlValueOf(d0.downloadTtlDays));

  /** What the form asks, for the mapping selects and the part editors. */
  const [allFields, setAllFields] = useState<{ key: string; label: string }[]>([]);
  const [signatureKeys, setSignatureKeys] = useState<string[]>([]);
  const [scopes, setScopes] = useState<ScopeOption[]>([]);
  const [sections, setSections] = useState<{ id: string; title: string }[]>([]);
  /**
   * The answers arranged the way the form is built — every picker on this
   * tab draws these, so two "Email"s are told apart here as they are on Logic.
   * `answerGroups` is the main form; `scopeGroups` one list per repeating
   * container; `allGroups` both, for a picker that may name either.
   */
  const [answerGroups, setAnswerGroups] = useState<OptGroup[]>([]);
  const [scopeGroups, setScopeGroups] = useState<Record<string, OptGroup[]>>({});
  const [allGroups, setAllGroups] = useState<OptGroup[]>([]);
  /** The published questions, for naming a branch condition in words. */
  const [publishedFields, setPublishedFields] = useState<FieldDefinition[]>([]);
  /** Questions set Hidden on the form — what the Answers part offers to leave off. */
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);

  const [graph, setGraph] = useState<WorkflowGraph | null>(null);
  const [connections, setConnections] = useState<ConnectionRow[]>([]);

  /**
   * The forms this one continues — and theirs, back up the chain — whose
   * answers the document can print as `parent.<key>`, `parent.parent.<key>`,
   * the way a workflow step and a Logic rule read them. Without a signature:
   * its answer is a stored file's address, and the document draws only this
   * form's own signatures.
   */
  const [parents, setParents] = useState<ParentForm[]>([]);

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const smartOff = !features.smartPdf;

  // ---- the R5 layout's own state -----------------------------------------
  const narrow = useMediaQuery(NARROW);
  const phone = useMediaQuery(PHONE);
  /** The page fills the viewport under the form bar, so the Outline, the
   *  canvas and the inspector each scroll on their own (like Fields). Off on a
   *  phone, where the inspector stacks under the card and the page scrolls. */
  const pageRef = useRef<HTMLFormElement>(null);
  usePageHeight(pageRef, !phone);
  /** The page on the canvas (1-based, the template's numbering). */
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState<PlacementSel>(null);
  const [armed, setArmed] = useState<Armed>(null);
  /** What the middle column shows on a form with a PDF: the page with its boxes, or the rendered preview. */
  const [view, setView] = useState<'layout' | 'preview'>('layout');
  /** The Outline's selection: the field the Place button is about. */
  const [outlineSel, setOutlineSel] = useState<BuilderSel>(null);
  /** Under 900 the chips rail and the inspector are bottom sheets; this is the open one. */
  const [rail, setRail] = useState<'fields' | 'props' | null>(null);
  const bSections = (draft?.sections || []) as BSection[];
  const bFields = (draft?.fields || []) as BField[];
  const renames = draft?.renames || [];
  const showLayout = !!template && view === 'layout';

  // ---- what a save sends, and the guard over it --------------------------
  //
  // NOT the template: the upload is its own save, and counting it here would
  // leave the screen dirty the moment a file finished uploading.
  const snapshot = useMemo(
    () => ({ edits, blocks, filename, ttl }),
    [edits, blocks, filename, ttl]
  );
  const { dirty, markSaved } = useDirty(snapshot);
  const guard = useUnsavedGuard({
    dirty, busy: saving,
    what: 'Your changes to this form’s document',
    onSave: () => save(),
  });

  useEffect(() => {
    getFormBuilder(form.id)
      .then((b) => {
        // Payment carries no answer and a content block is not a question, so
        // neither is offered as something a document can print.
        const answerable = b.fields.filter((f) => f.input_type !== 'payment' && !asksNothing(f.input_type));
        setAllFields(answerable.map((f) => ({ key: f.key, label: f.label })));
        setSignatureKeys(answerable.filter((f) => f.input_type === 'signature').map((f) => f.key));
        setPublishedFields(b.fields);
        setHiddenKeys(answerable.filter((f) => f.ui_meta?.showFlag === '__hidden').map((f) => f.key));

        // Every node of every widget tree, nested ones included: "devices" is
        // as much a table as "locations" is, and usually the more useful one.
        const walk = (n: { scope: string; itemLabel?: string; children?: unknown[] }, out: ScopeOption[]) => {
          out.push({
            scope: n.scope,
            label: n.itemLabel || n.scope,
            fields: answerable.filter((f) => f.scope === n.scope).map((f) => ({ key: f.key, label: f.label })),
          });
          const mine = out.length - 1;
          for (const c of (n.children || []) as typeof n[]) walk(c, out);
          // Everything walked since is nested in this one — what a block of it can count.
          out[mine].inside = out.slice(mine + 1).map((s) => s.scope);
        };
        const found: ScopeOption[] = [];
        for (const w of b.widgets || []) if (w.tree) walk(w.tree, found);
        setScopes(found);
        setSections((b.layout?.sections || []).map((sec) => ({ id: sec.id, title: sec.title })));
        const secInfo = sectionsFromLayout(b.layout);
        setAnswerGroups(pickerGroups(answerable, secInfo, [], undefined, { repeat: false }));
        setAllGroups(pickerGroups(answerable, secInfo, b.widgets || []));
        const byScope: Record<string, OptGroup[]> = {};
        for (const sc of found) byScope[sc.scope] = groupsForContainer(sc.scope, answerable, secInfo, undefined, { strayLabel: 'Questions' });
        setScopeGroups(byScope);
      })
      .catch(() => { setAllFields([]); setSignatureKeys([]); setScopes([]); setAnswerGroups([]); setScopeGroups({}); setAllGroups([]); });
  }, [form.id]);

  useEffect(() => {
    const printable = (list: ParentForm[]): ParentForm[] => list.map((p) => ({
      ...p,
      fields: p.fields.filter((f) => f.input_type !== 'signature'),
      ...(p.parents ? { parents: printable(p.parents) } : {}),
    }));
    getFormParents(form.id).then((r) => setParents(printable(r.parents || []))).catch(() => setParents([]));
  }, [form.id]);
  /** The earlier forms' questions, as picker groups and as labels. */
  const parentGroups = useMemo(() => parentOptGroups(parents), [parents]);
  const parentDefs = useMemo(() => parentFieldDefs(parents), [parents]);
  const parentTokens = useMemo<FieldGroup[]>(
    () => parentGroups.map((g) => ({ label: g.label, options: g.opts.map((o) => ({ key: o.v, label: o.l })) })),
    [parentGroups]
  );
  /** The same answers as the Outline lists them, under the form's own pages. */
  const earlierOutline = useMemo(
    () => parentGroups.map((g) => ({ label: g.label, items: g.opts.map((o) => ({ key: o.v, label: o.l })) })),
    [parentGroups]
  );
  const earlierLabels = useMemo(
    () => new Map(parentGroups.flatMap((g) => g.opts.map((o) => [o.v, o.l] as const))),
    [parentGroups]
  );
  /** A mapped PDF field can name an earlier form's answer as well as this form's. */
  const acroGroups = useMemo(() => [...allGroups, ...parentGroups], [allGroups, parentGroups]);

  // Where this document goes. Both halves of the answer live on Workflow —
  // the drawing says which steps attach it and which endings offer it — so
  // both are read from there rather than guessed from the connection list.
  useEffect(() => {
    getFormWorkflow(form.id).then((r) => setGraph(r.workflow?.graph || null)).catch(() => setGraph(null));
    listFormConnections(form.id).then((r) => setConnections(r.connections)).catch(() => setConnections([]));
  }, [form.id]);

  const filenameGroups = useMemo(
    () => [...filenameTokenGroups(answerGroups, publishedFields), ...parentTokens],
    [answerGroups, publishedFields, parentTokens]);
  const [filenameRef, insertIntoFilename] = useCaretInsert(filename, setFilename);

  const ctx: DocBlockContext = useMemo(() => ({
    templates,
    hiddenKeys,
    fields: allFields,
    signatureKeys,
    scopes,
    sections,
    answerGroups,
    scopeGroups,
    allGroups,
    tokenGroups: [...tokenGroupsFor(answerGroups, scopes), ...parentTokens],
  }), [templates, hiddenKeys, allFields, signatureKeys, scopes, sections, answerGroups, scopeGroups, allGroups, parentTokens]);

  // ---- keys: the document names LIVE keys, the Outline shows the draft's ----
  //
  // A field renamed in the draft since the last publish keeps its live key in
  // the stored document until Publish renames it there too (R5, the publish
  // route). So everything here that reads the document maps through the
  // renames, and everything that writes it maps back.
  const labelOf = useCallback((liveKey: string) => {
    // An earlier form's answer is named for that form, never renamed here.
    const earlier = parentDefs.find((f) => f.key === liveKey);
    if (earlier) return earlier.label || liveKey;
    const draftKey = draftKeyOf(liveKey, renames);
    const root = draftKey.split('.')[0];
    const f = bFields.find((x) => x.key === root && !x.slot && !x.cardId && !x.nodeScope);
    if (f) return draftKey === root ? (f.label || f.key) : `${f.label || f.key} › ${draftKey.slice(root.length + 1)}`;
    return allFields.find((x) => x.key === liveKey)?.label || liveKey;
  }, [bFields, allFields, renames, parentDefs]);
  const liveKeys = useMemo(() => new Set(allFields.map((f) => f.key)), [allFields]);
  const placementsForOutline = useMemo(
    () => placements.map((p) => ({ fieldKey: draftKeyOf(p.fieldKey, renames), page: p.page })),
    [placements, renames]
  );

  /**
   * What the Outline's selection can put on the page: a published, answerable
   * field (as its live key), a repeating container the published form knows,
   * or nothing — with the reason, which the Place button says.
   */
  const placeable = useMemo((): { kind: 'field' | 'scope'; key: string; label: string } | { kind: null; why: string; label: string } => {
    if (!outlineSel) return { kind: null, why: 'Pick a field in the Outline first.', label: '' };
    if (outlineSel.t === 'field') {
      // An earlier form's answer: published there, and placed under the key it is listed by.
      const earlier = earlierLabels.get(outlineSel.key);
      if (earlier !== undefined) return { kind: 'field', key: outlineSel.key, label: labelOf(outlineSel.key) };
      const f = bFields.find((x) => x.key === outlineSel.key);
      const label = f?.label || outlineSel.key;
      const live = liveKeyOf(outlineSel.key, renames);
      if (f && asksNothing(f.type)) return { kind: null, why: 'That is not an answer, so there is nothing to print.', label };
      if (f?.type === 'payment') return { kind: null, why: 'A payment has no answer to print.', label };
      if (!liveKeys.has(live)) return { kind: null, why: 'Publish the form first — this field is not live yet.', label };
      return { kind: 'field', key: live, label };
    }
    if (outlineSel.t === 'wnode') {
      const sc = scopes.find((s) => s.scope === outlineSel.scope);
      if (!sc) return { kind: null, why: 'Publish the form first — this container is not live yet.', label: outlineSel.scope };
      return { kind: 'scope', key: sc.scope, label: sc.label };
    }
    return { kind: null, why: 'A section or card is not placed as one thing — pick a field inside it.', label: '' };
  }, [outlineSel, bFields, liveKeys, renames, scopes, earlierLabels, labelOf]);

  /** The Outline's drag payload: what `PlacementCanvas` reads on a drop. */
  const dragDataFor = useCallback((s: BuilderSel): string | null => {
    if (!template || smartOff || !s) return null;
    if (s.t === 'field') {
      if (earlierLabels.has(s.key)) return `field:${s.key}`;
      const f = bFields.find((x) => x.key === s.key);
      if (!f || asksNothing(f.type) || f.type === 'payment') return null;
      const live = liveKeyOf(s.key, renames);
      return liveKeys.has(live) ? `field:${live}` : null;
    }
    if (s.t === 'wnode') return scopes.some((x) => x.scope === s.scope) ? `scope:${s.scope}` : null;
    return null;
  }, [template, smartOff, bFields, renames, liveKeys, scopes, earlierLabels]);

  const setBoxes = (next: Placement[], nextRegions: Region[]) => { setPlacements(next); setRegions(nextRegions); };

  /** Where the next box on this page lands: an inch in, under the last one placed. */
  const nextTop = () => {
    const onThisPage = placements.filter((p) => p.page === page).length + regions.filter((r) => r.page === page).length;
    return PLACE_TOP + (onThisPage % 12) * PLACE_STEP;
  };

  /** An answer's box on the current page, selected. */
  const placeField = (key: string) => {
    if (!template) return;
    setPlacements((list) => [...list, newPlacement(key, page, template.height, PLACE_X, nextTop(), signatureKeys.includes(key))]);
    setSel({ kind: 'placement', index: placements.length });
    setView('layout');
  };

  /** The Place button: the selection lands on the current page, an inch in, under the last one placed. */
  const placeSelected = () => {
    if (!template || placeable.kind === null) return;
    if (placeable.kind === 'field') { placeField(placeable.key); return; }
    const sc = scopes.find((s) => s.scope === placeable.key);
    setRegions((list) => [...list, newRegion(placeable.key, page, template.height, PLACE_X, nextTop(), sc?.fields[0]?.key)]);
    setSel({ kind: 'region', index: regions.length });
    setView('layout');
  };

  // ---- saving -------------------------------------------------------------

  async function save() {
    setSaving(true); setError('');
    try {
      // Into the DRAFT: what respondents get does not change until Publish.
      await saveDraftDocument(form.id, {
            filename: filename.trim() || undefined,
            // Always, even empty: an emptied arrangement is a clear, and the
            // server reads an ABSENT list as "keep what is stored".
            blocks,
            // `?? 30` would be wrong here: "until the submission is deleted"
            // IS null, and a nullish default would quietly turn the one
            // choice that means "no expiry" into thirty days.
            downloadTtlDays: (TTL_CHOICES.find((c) => c.value === ttl) || TTL_CHOICES[1]).days,
            // Only what a save may change travels. Which PDFs there are, their
            // `url`, `pages` and page size are facts the uploads established by
            // reading them, and the server takes them from what it has stored
            // regardless of what is sent here — see sanitizeTemplate. And no
            // `mode`: it is derived from these two things, which is the whole point.
            ...(templates.length ? { templates: withEdits } : {}),
      });
      await refreshForm();
      noteDocumentDraft();
      markSaved();
      toast('Document saved into the draft — publish to make it live.');
      void loadPreview();
    } catch (err: any) {
      setError(err.message || 'Save failed');
      // Rethrown so "Save and leave" stays put on a failure rather than
      // leaving with the very changes the save could not store.
      throw err;
    } finally {
      setSaving(false);
    }
  }

  // ---- the PDFs -----------------------------------------------------------

  /**
   * The PDFs as the server now holds them, after an add, a replace or a
   * remove. Every other PDF keeps what is on screen for it, saved or not;
   * `changed` (the one just stored) starts from `edit`, or from what the
   * server gave it.
   */
  function applyTemplates(
    document: FormSettingsView['document'], changed?: FormPdfTemplate, edit?: TemplateEdit, base: DocBlock[] = blocks
  ): DocBlock[] {
    const list = templatesOf(document);
    // The parts on screen get what the server did to the stored ones — the
    // same function over the same PDFs (`arrangePages`): a new PDF's pages at
    // the end, a gone one's parts dropped, a grown or cut one kept in step.
    // Unsaved edits to the parts stay unsaved; the rest now matches.
    const next = arrangePages(base, templates, list) as DocBlock[];
    setBlocks(next);
    setTemplates(list);
    setEdits((m) => Object.fromEntries(list.map((t) => {
      const id = t.id as string;
      if (changed && id === changed.id) return [id, edit || editOf(changed)];
      return [id, m[id] || editOf(t)];
    })));
    return next;
  }

  /** Put one PDF on the canvas, at the first of its pages the document keeps. */
  function showTemplate(id: string | undefined) {
    const t = templates.find((x) => x.id === id);
    setActiveId(id);
    setPage(t ? keptPagesOf(t)[0] : 1);
    setSel(null);
    setView('layout');
  }

  async function onTemplate(file: File, replaceId?: string) {
    setError('');
    setUploading(true);
    try {
      const { template: next, document } = await uploadFormTemplate(form.id, file, replaceId);
      // Positions belong to the page they were placed on. A different document
      // means different pages, so keeping them would scatter boxes across
      // somebody else's layout. (A replace keeps the PDF's id, so the parts
      // drawing on it still do.)
      applyTemplates(document, next);
      showTemplate(next.id);
      await refreshForm();
      noteDocumentDraft();
      // The upload IS the save — the server already holds exactly this — so
      // the unsaved guard must not count it. Unless other edits were already
      // pending, which stay somebody's to save.
      if (!dirty) markSaved();
      const joined = replaceId ? ''
        : ` Its ${next.pages === 1 ? 'page is' : `${next.pages} pages are`} at the end of the pages below — drag them where they go.`;
      toast(next.acroFields?.length
        ? `Uploaded — ${next.acroFields.length} named field(s) found. Check the mapping below.${joined}`
        : `Uploaded. It has no named fields, so every answer is positioned on the page.${joined}`);
      void loadPreview();
    } catch (err: any) {
      setError(err.message || 'The PDF could not be uploaded.');
    } finally {
      setUploading(false);
    }
  }

  /** A blank page to place answers on — a PDF the server makes, empty. */
  /**
   * One more blank page — `at` is where its part goes in the list (absent: where
   * the arrangement puts it, after the blank's other pages or at the end).
   *
   * A document keeps ONE blank PDF and grows it a page at a time, rather than
   * a file per page: each page is still its own part, placed anywhere, and the
   * canvas shows them as that PDF's page tabs.
   */
  async function onAddBlank(at?: number) {
    setError('');
    setUploading(true);
    try {
      const blank = templates.find((t) => t.blank);
      const mine = blank ? (edits[blank.id as string] || editOf(blank)) : undefined;
      if (blank && blank.pages >= MAX_BLANK_PAGES) {
        setError(`A blank PDF holds up to ${MAX_BLANK_PAGES} pages.`);
        return;
      }
      const { template: next, document } = await addBlankTemplate(
        form.id, blank ? { pages: blank.pages + 1, replace: blank.id } : { pages: 1 }
      );
      // Asked for at a place: a part taking the whole blank is split into its
      // pages first, or the new page would simply join it, wherever it is.
      let base = blocks;
      let where = at;
      if (blank && at !== undefined) {
        const whole = blocks.findIndex((b) => b.type === 'template' && b.templateId === blank.id && !b.pages?.length);
        if (whole >= 0) {
          const pages = pagePartsOf({ id: blank.id, pages: blank.pages }) as DocBlock[];
          base = [...blocks.slice(0, whole), ...pages, ...blocks.slice(whole + 1)];
          if (whole < at) where = at + pages.length - 1;
        }
      }
      let parts = applyTemplates(document, next, mine, base);
      const newPage = next.pages;
      if (where !== undefined) {
        const at = where;
        const from = parts.findIndex((b) => b.type === 'template' && b.templateId === next.id
          && b.pages?.length === 1 && b.pages[0] === newPage);
        if (from >= 0 && from !== at) {
          parts = parts.slice();
          const [moved] = parts.splice(from, 1);
          // `at` counts the list as it was, which is the list without the new page.
          parts.splice(at, 0, moved);
          setBlocks(parts);
        }
      }
      showTemplate(next.id);
      setPage(newPage);
      await refreshForm();
      noteDocumentDraft();
      if (!dirty && at === undefined) markSaved();
      toast(`Blank page added${at === undefined ? '' : ' where you asked'} — place answers on it.`
        + (at === undefined ? '' : ' Save to keep it there.'));
      void loadPreview();
    } catch (err: any) {
      setError(err.message || 'The page could not be added.');
    } finally {
      setUploading(false);
    }
  }

  /** How many pages a blank has. Answers on pages that go, go with them — said first (`shrinking`). */
  function onBlankPages(t: FormPdfTemplate, pages: number, confirmed = false) {
    const id = t.id as string;
    const mine = edits[id] || editOf(t);
    const lost = mine.placements.filter((p) => p.page > pages).length + mine.regions.filter((r) => r.page > pages).length;
    if (lost && !confirmed) { setShrinking({ id, pages, lost }); return; }
    void (async () => {
      setError('');
      setUploading(true);
      try {
        const { template: next, document } = await addBlankTemplate(form.id, { pages, replace: id });
        // What is on screen for it, less the pages that are gone — including
        // positions not saved yet, which the server has never seen.
        applyTemplates(document, next, {
          acroMap: mine.acroMap,
          placements: mine.placements.filter((p) => p.page <= pages),
          regions: mine.regions.filter((r) => r.page <= pages),
        });
        if (id === template?.id && page > pages) setPage(1);
        setSel(null);
        await refreshForm();
        noteDocumentDraft();
        if (!dirty) markSaved();
        void loadPreview();
      } catch (err: any) {
        setError(err.message || 'The page count could not be changed.');
      } finally {
        setUploading(false);
      }
    })();
  }

  async function onRemoveTemplate(t: FormPdfTemplate) {
    setError('');
    try {
      const { document } = await deleteFormTemplate(form.id, t.id);
      // The server dropped the parts that drew on it; so does the screen, or
      // the next save would send them back to be dropped again.
      const first = templates[0]?.id;
      const drawing = blocks.filter((b) => b.type === 'template' && (b.templateId || first) === t.id).length;
      applyTemplates(document);
      if (template?.id === t.id) showTemplate(templatesOf(document)[0]?.id);
      await refreshForm();
      noteDocumentDraft();
      if (!dirty) markSaved();
      toast(drawing
        ? `Removed, with the ${drawing} part${drawing === 1 ? '' : 's'} that drew on it.`
        : 'Removed.', { kind: 'info' });
      void loadPreview();
    } catch (err: any) {
      setError(err.message || 'The PDF could not be removed.');
    }
  }

  /**
   * Replacing a PDF, with what it costs said first (rule 5).
   *
   * It used to clear the positions silently. Somebody who has spent twenty
   * minutes placing fourteen boxes on a five-page contract deserves to be told
   * that picking a new file throws them away — before it happens, and with the
   * number in it.
   */
  function onReplace(t: FormPdfTemplate, file: File) {
    const mine = edits[t.id as string] || NO_EDIT;
    if (mine.placements.length || mine.regions.length) setReplacing({ file, id: t.id as string });
    else void onTemplate(file, t.id);
  }

  // ---- the parts ----------------------------------------------------------

  const move = (index: number, by: -1 | 1) => {
    const to = index + by;
    if (to < 0 || to >= blocks.length) return;
    setBlocks((list) => {
      const next = list.slice();
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  /** A part dragged to sit before `to` (`to` === length: at the end). */
  const moveTo = (from: number, to: number) => {
    if (from === to || from + 1 === to) return;
    setBlocks((list) => {
      const next = list.slice();
      const [moved] = next.splice(from, 1);
      next.splice(from < to ? to - 1 : to, 0, moved);
      return next;
    });
  };

  /** One part of several pages into a part per page, in its order — each then moves on its own. */
  const splitPart = (index: number) => {
    const b = blocks[index];
    if (b?.type !== 'template') return;
    const t = templateForPart(templates, b);
    if (!t) return;
    const pages = b.pages?.length ? b.pages : Array.from({ length: t.pages }, (_, i) => i + 1);
    setBlocks((list) => [
      ...list.slice(0, index),
      ...pages.map((p) => ({ type: 'template' as const, templateId: t.id, pages: [p] })),
      ...list.slice(index + 1),
    ]);
  };

  // ---- each page part's picture ----------------------------------------
  //
  // A list of "page 1 · page 2 · page 3" rows is a list of numbers; a small
  // picture of each is what lets somebody put the pages in order. Drawn once
  // per PDF a part shows.
  const [thumbs, setThumbs] = useState<Record<string, string[]>>({});
  const asked = useRef(new Set<string>());
  useEffect(() => {
    for (const b of blocks) {
      if (b.type !== 'template') continue;
      const t = templateForPart(templates, b);
      if (!t || t.blank || asked.current.has(t.url)) continue;
      const url = t.url;
      asked.current.add(url);
      pageThumbnails(url, 64)
        .then((pics) => setThumbs((m) => ({ ...m, [url]: pics })))
        .catch(() => setThumbs((m) => ({ ...m, [url]: [] })));
    }
  }, [blocks, templates]);

  // ---- dragging a part (the tap path is Move up / Move down in its ⋯) ----
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);

  const addPart = (kind: DocBlockKind) => {
    let fresh = newBlock(kind, ctx);
    setAdding(false);
    if (!fresh) return;
    // With nothing arranged the document is every PDF, whole, in order — and
    // the first part replaces that. So it starts FROM that: adding the answers
    // to a form with a cover and a terms PDF reads cover · terms · answers,
    // one Move up from what was meant, rather than the answers alone with both
    // PDFs silently gone.
    const base: DocBlock[] = blocks.length || !templates.length
      ? blocks : templates.flatMap((t) => pagePartsOf({ id: t.id, pages: t.pages }) as DocBlock[]);
    if (fresh.type === 'template') {
      if (!blocks.length) { setBlocks(base); setEditingPart(0); return; }
      // A new template part takes a PDF no part draws on yet, when there is one.
      const first = templates[0]?.id;
      const used = new Set(blocks.flatMap((b) => (b.type === 'template' ? [b.templateId || first] : [])));
      const spare = templates.find((t) => !used.has(t.id));
      if (spare) fresh = { type: 'template', templateId: spare.id };
    }
    setBlocks([...base, fresh]);
    setEditingPart(base.length);
  };

  /**
   * A template page that appears in no part.
   *
   * Worth saying because the way it usually happens is somebody choosing pages
   * 1–3 and 5, and the page they left out is the signature page.
   */
  const unusedTemplatePage = useMemo((): { t: FormPdfTemplate; page: number | null } | null => {
    const parts = blocks.filter((b) => b.type === 'template') as { templateId?: string; pages?: number[] }[];
    if (!parts.length) return null;
    // …and a whole PDF that appears in none, which is how a second upload
    // onto an arranged document usually is: there, and in nothing.
    const first = templates[0]?.id;
    for (const t of templates) {
      const mine = parts.filter((p) => (p.templateId || first) === t.id);
      if (!mine.length) return { t, page: null };
      if (mine.some((p) => !p.pages?.length)) continue;   // one part takes them all
      const used = new Set(mine.flatMap((p) => p.pages || []));
      for (let p = 1; p <= t.pages; p += 1) if (!used.has(p)) return { t, page: p };
    }
    return null;
  }, [blocks, templates]);

  /**
   * The pages of the PDF on the canvas that the document KEEPS, in the order
   * its parts put them — what Layout offers to place answers on. A page taken
   * out of the arrangement is not printed, so it is not offered; with no
   * arrangement (or a PDF no part draws on yet) it is every page, in order.
   * The numbers stay the PDF’s own, as the parts list names them.
   */
  const keptPagesOf = useCallback((t: FormPdfTemplate): number[] => {
    const all = Array.from({ length: t.pages }, (_, i) => i + 1);
    const first = templates[0]?.id;
    const kept: number[] = [];
    for (const b of blocks) {
      if (b.type !== 'template' || (b.templateId || first) !== t.id) continue;
      for (const p of b.pages?.length ? b.pages : all) if (p >= 1 && p <= t.pages && !kept.includes(p)) kept.push(p);
    }
    return kept.length ? kept : all;
  }, [blocks, templates]);
  const layoutPages = useMemo(() => (template ? keptPagesOf(template) : []), [template, keptPagesOf]);
  // The tab opens on the first page the document prints, and the page on the
  // canvas is always one it keeps — a page taken out while it was showing
  // hands over to the first that is left.
  const openedOn = useRef(false);
  useEffect(() => {
    if (!layoutPages.length) return;
    if (!openedOn.current || !layoutPages.includes(page)) setPage(layoutPages[0]);
    openedOn.current = true;
  }, [layoutPages, page]);

  // ---- the preview --------------------------------------------------------

  const [pages, setPages] = useState<string[] | null>(null);
  const [pvPage, setPvPage] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState('');
  /** Bumped per attempt so a slow render cannot overwrite a newer one. */
  const previewRun = useRef(0);

  const loadPreview = useCallback(async () => {
    const run = (previewRun.current += 1);
    setPages(null);
    setPreviewError('');
    try {
      const blob = await previewFormDocument(form.id, {
        blocks,
        ...(templates.length ? { templates: withEdits } : {}),
      });
      if (run !== previewRun.current) return;
      const url = URL.createObjectURL(blob);
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
      const thumbs = await pageThumbnails(url, 760);
      if (run !== previewRun.current) return;
      setPages(thumbs);
      setPvPage((p) => Math.min(p, Math.max(0, thumbs.length - 1)));
    } catch (e) {
      if (run !== previewRun.current) return;
      setPages([]);
      setPreviewError((e as Error).message || 'The preview could not be produced.');
    }
  }, [form.id, blocks, templates, withEdits]);

  // Once, on arrival, against what is STORED — "updates when you save" is what
  // the card promises, and a preview that re-rendered on every keystroke would
  // be a Chromium run per character.
  useEffect(() => { void loadPreview(); }, [form.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // ---- where it goes ------------------------------------------------------

  const uses = useMemo(
    () => documentUses(graph, connections, publishedFields),
    [graph, connections, publishedFields]
  );

  // ---- the screen ---------------------------------------------------------

  const placedCount = placements.length + regions.length;
  const outlineCount = template ? (placedCount ? `${placedCount} placed · click, then Place` : 'click, then Place') : undefined;
  /** Select a placed thing from the list: on its page, in the layout view. */
  const pickPlaced = (s: PlacementSel) => {
    setSel(s);
    if (!s) return;
    const on = s.kind === 'placement' ? placements[s.index]?.page : regions[s.index]?.page;
    if (on) setPage(on);
    setView('layout');
    if (narrow) setRail(null);
  };

  const previewPane = (
    <div className="dx-preview">
      <div
        className={`fd-page${pages?.[pvPage] ? '' : ' empty'}`}
        style={pages?.[pvPage] ? undefined : { aspectRatio: '612 / 792' }}
      >
        {pages?.[pvPage]
          ? <img src={pages[pvPage]} alt={`Page ${pvPage + 1} of the document`} />
          : <span>{pages ? (previewError || 'no preview') : 'rendering…'}</span>}
      </div>
      <div className="fd-pager">
        <button type="button" className="fs-btn neutral sm" disabled={pvPage <= 0}
          aria-label="Previous page" onClick={() => setPvPage((p) => p - 1)}>‹</button>
        <span className="of">{pages?.length ? `${pvPage + 1} / ${pages.length}` : '—'}</span>
        <button type="button" className="fs-btn neutral sm"
          disabled={!pages?.length || pvPage >= pages.length - 1}
          aria-label="Next page" onClick={() => setPvPage((p) => p + 1)}>›</button>
        <span style={{ flex: 1 }} />
        <button type="button" className="fs-btn neutral sm" onClick={() => void loadPreview()}>
          Preview with sample answers
        </button>
        {previewUrl && (
          <a className="fs-btn neutral sm" href={previewUrl} target="_blank" rel="noreferrer"
            style={{ textDecoration: 'none' }}>Open PDF ↗</a>
        )}
      </div>
    </div>
  );

  return (
    <form className="dx-page" ref={pageRef} onSubmit={(e) => { e.preventDefault(); void save().catch(() => undefined); }}>
      <h1 className="fb-vh">Document</h1>
      {error && <div className="ad-err" style={{ margin: '8px 16px 0' }}>{error}</div>}
      <div className="dx-main">
        {/* ---- the Outline: pick a field, see where it is placed ---- */}
        {!narrow && draft && (
          <div className="dx-side">
            <BuilderOutline
              sections={bSections} fields={bFields} widgets={draft.widgets} rules={draft.rules}
              tagMode="document" countLabel={outlineCount} placements={placementsForOutline}
              sel={outlineSel} onSelect={setOutlineSel} dragData={dragDataFor} earlier={earlierOutline}
            />
          </div>
        )}

        {/* ---- the middle: the PDF page with its boxes, or the rendered preview ---- */}
        <div className="dx-canvas">
          <div className="dx-toolbar">
            {template && (
              <div className="fs-seg dx-view" role="group" aria-label="View">
                <button type="button" className={view === 'layout' ? 'on' : ''} aria-pressed={view === 'layout'} onClick={() => setView('layout')}>Layout</button>
                <button type="button" className={view === 'preview' ? 'on' : ''} aria-pressed={view === 'preview'} onClick={() => setView('preview')}>Preview</button>
              </div>
            )}
            {showLayout && multi && (
              <select
                className="dx-which" aria-label="Which PDF is on the page" value={template.id}
                onChange={(e) => showTemplate(e.target.value)}
              >
                {templates.map((t) => <option key={t.id} value={t.id}>{templateName(t)}</option>)}
              </select>
            )}
            {showLayout && layoutPages.length > 1 && (
              <div className="fb-pagetabs" role="tablist" aria-label="Template pages">
                {layoutPages.map((p) => (
                  <button key={p} type="button" role="tab" aria-selected={p === page} className={p === page ? 'on' : ''}
                    onClick={() => { setPage(p); setSel(null); }}>
                    Page {p}
                  </button>
                ))}
              </div>
            )}
            <span className="dx-toolbar-note">
              {showLayout
                ? (narrow ? 'Tap a chip, then tap the page where it goes. Drag a box to move it.' : 'Drag the boxes to move answers. Pick a field in the Outline, then Place it — or drag it onto the page.')
                : pages?.length
                  ? `Page ${pvPage + 1} of ${pages.length}, with sample answers. Updates when you save.`
                  : 'With sample answers. Updates when you save.'}
            </span>
            {narrow && !phone && (
              <div className="pe-toggles">
                {template && (
                  <button type="button" className="fs-btn sm" aria-expanded={rail === 'fields'}
                    onClick={() => setRail(rail === 'fields' ? null : 'fields')}>
                    Fields{armed ? ' · 1 armed' : ''}
                  </button>
                )}
                <button type="button" className="fs-btn sm" aria-expanded={rail === 'props'}
                  onClick={() => setRail(rail === 'props' ? null : 'props')}>
                  Properties
                </button>
                {/* Save stays one click away while the inspector is a sheet. */}
                <button className="fs-btn primary sm" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save document'}
                </button>
              </div>
            )}
          </div>
          <div className="dx-scroll">
            {showLayout && phone ? (
              // Not a thing a finger can do on a 375px page (§4E E3).
              // Everything already positioned is kept; this only declines to
              // let a phone aim.
              <div className="pe-nophone">
                <b>Position the answers on a tablet or a computer</b>
                Placing a box means aiming at a few millimetres of a page, which a phone
                cannot do accurately. {placedCount
                  ? `The ${placedCount} position${placedCount === 1 ? '' : 's'} already saved are untouched.`
                  : 'Nothing is positioned yet.'}
              </div>
            ) : showLayout ? (
              <PlacementCanvas
                template={{ ...template, acroMap }}
                labelOf={labelOf} scopes={scopes} signatureKeys={signatureKeys}
                placements={placements} regions={regions} onChange={setBoxes}
                page={page} onPage={setPage} sel={sel} onSel={setSel}
                armed={armed} onArmed={setArmed} narrow={narrow} readOnly={smartOff}
              />
            ) : previewPane}
          </div>
        </div>

        {/* ---- the inspector ---- */}
        {narrow && rail && <div className="pe-scrim" onClick={() => setRail(null)} />}
        {narrow && !phone && template && (
          <aside className="pe-fields" data-open={String(rail === 'fields')}>
            <div className="pe-overline">Fields</div>
            <div className="pe-hint">Tap one, then tap the page where it goes.</div>
            {allFields.map((f) => (
              <div
                key={f.key}
                className={`pe-chip ${placements.some((p) => p.fieldKey === f.key) ? 'is-placed' : ''}${armed?.kind === 'field' && armed.key === f.key ? ' is-armed' : ''}`}
                onClick={() => { setArmed({ kind: 'field', key: f.key }); setRail(null); }}
              >
                {f.label || f.key}
              </div>
            ))}
            {!allFields.length && <div className="pe-hint">This form has no fields yet.</div>}
            {scopes.length > 0 && (
              <>
                <div className="pe-overline" style={{ marginTop: 18 }}>Tables</div>
                <div className="pe-hint">One row per item. Tap where its first row starts.</div>
                {scopes.map((sc) => (
                  <div
                    key={sc.scope}
                    className={`pe-chip pe-chip-table ${regions.some((r) => r.scope === sc.scope) ? 'is-placed' : ''}${armed?.kind === 'scope' && armed.key === sc.scope ? ' is-armed' : ''}`}
                    onClick={() => { setArmed({ kind: 'scope', key: sc.scope }); setRail(null); }}
                  >
                    {sc.label}
                  </div>
                ))}
              </>
            )}
          </aside>
        )}
        <aside className="dx-panel" data-open={narrow && !phone ? String(rail === 'props') : 'true'} aria-label="Document settings">
          <div className="dx-panel-head">
            <b className="dx-panel-title">{template ? (multi ? 'The PDFs' : 'The PDF') : 'The document'}</b>
            <span className={`dx-dirty${dirty ? ' is-dirty' : ''}`}>{dirty ? 'Unsaved changes' : 'Saved in the draft'}</span>
            <button className="fs-btn primary sm" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save document'}
            </button>
          </div>
          <div className="dx-panel-body">
            {/* ---- 1 · Your own PDF ------------------------------------- */}
            <section className="dx-sec">
              {template ? (
                <>
                  {multi && <SectionLabel className="dx-label">Your PDFs · {templates.length}</SectionLabel>}
                  {templates.map((t) => {
                    const on = t.id === template.id;
                    const mine = edits[t.id as string] || editOf(t);
                    return (
                      <div key={t.id} className={`fd-well${multi && on ? ' is-on' : ''}`}>
                        <div className={`fd-glyph${t.blank ? ' blank' : ''}`}>{t.blank ? 'PAGE' : 'PDF'}</div>
                        <div className="who">
                          <b>{templateName(t)}</b>
                          <div className="fs-cell-sub">{factsOf(t, mine.acroMap)}</div>
                        </div>
                        <div className="acts">
                          {multi && !on && (
                            <button type="button" className="fs-btn neutral sm" onClick={() => showTemplate(t.id)}>
                              Place answers
                            </button>
                          )}
                          {t.blank ? (
                            <select
                              className="fd-pages-select" aria-label={`Pages in ${templateName(t)}`}
                              value={t.pages} disabled={uploading || smartOff}
                              onChange={(e) => onBlankPages(t, Number(e.target.value))}
                            >
                              {Array.from({ length: MAX_BLANK_PAGES }, (_, i) => i + 1).map((n) => (
                                <option key={n} value={n}>{n} page{n === 1 ? '' : 's'}</option>
                              ))}
                            </select>
                          ) : (
                            <>
                              <a className="fs-btn neutral sm" href={t.url} target="_blank" rel="noreferrer"
                                style={{ textDecoration: 'none' }}>View</a>
                              {/* Inert on a lapsed plan, with the reason. The server
                                  pins it either way; this keeps the screen honest. */}
                              <label
                                className={`fs-btn neutral sm${smartOff || uploading ? ' is-off' : ''}`}
                                style={{ cursor: smartOff ? 'not-allowed' : 'pointer', marginBottom: 0, opacity: smartOff ? 0.5 : 1 }}
                              >
                                {uploading ? 'Uploading…' : 'Replace…'}
                                <input
                                  type="file" accept="application/pdf,.pdf" hidden disabled={uploading || smartOff}
                                  aria-label={`Replace ${templateName(t)}`}
                                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onReplace(t, f); }}
                                />
                              </label>
                            </>
                          )}
                          <button type="button" className="fs-btn neutral sm" disabled={smartOff}
                            aria-label={multi ? `Remove ${templateName(t)}` : undefined}
                            onClick={() => void onRemoveTemplate(t)}>
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {!smartOff && (
                    <div className="fd-add-pdf">
                      <label
                        className={`fs-btn neutral sm${uploading || templates.length >= MAX_TEMPLATES ? ' is-off' : ''}`}
                        style={{ marginBottom: 0, cursor: 'pointer' }}
                        title={templates.length >= MAX_TEMPLATES ? `Up to ${MAX_TEMPLATES} PDFs and blank pages` : undefined}
                      >
                        + Add another PDF
                        <input
                          type="file" accept="application/pdf,.pdf" hidden
                          disabled={uploading || templates.length >= MAX_TEMPLATES}
                          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void onTemplate(f); }}
                        />
                      </label>
                      <button type="button" className="fs-btn neutral sm"
                        disabled={uploading || templates.length >= MAX_TEMPLATES} onClick={() => void onAddBlank()}>
                        + Add a blank page
                      </button>
                    </div>
                  )}
                  {smartOff && <PaidLine />}

                  {/* ---- Place the Outline's selection ---- */}
                  <button
                    type="button" className="fs-btn primary dx-place" disabled={smartOff || placeable.kind === null || phone}
                    title={smartOff ? 'Your own PDF is on every paid plan' : placeable.kind === null ? placeable.why : undefined}
                    onClick={placeSelected}
                  >
                    {placeable.kind === 'scope'
                      ? <>Place a table of “{placeable.label}” on {multi ? templateName(template) : 'the PDF'}</>
                      : placeable.label ? <>Place “{placeable.label}” on {multi ? templateName(template) : 'the PDF'}</>
                        : `Place on ${multi ? templateName(template) : 'the PDF'}`}
                  </button>
                  {!smartOff && placeable.kind === null && <div className="hint" style={{ marginTop: -4 }}>{placeable.why}</div>}

                  {/* ---- …or an answer from a form this one continues ---- */}
                  {parentGroups.length > 0 && !phone && (
                    <FieldPicker
                      value="" groups={parentGroups} disabled={smartOff} className="fs-input"
                      placeholder="Place an answer from an earlier form…"
                      ariaLabel={`Place an answer from an earlier form on ${multi ? templateName(template) : 'the PDF'}`}
                      onChange={(v) => { if (v) placeField(v); }}
                    />
                  )}

                  {/* ---- Placed answers · n ---- */}
                  <SectionLabel className="dx-label">
                    {multi ? <>Placed on {templateName(template)} · {placedCount}</> : <>Placed answers · {placedCount}</>}
                  </SectionLabel>
                  {placedCount === 0 && <div className="fs-cell-sub">Nothing placed yet.</div>}
                  {placedCount > 0 && (
                    <ul className="dx-placed">
                      {placements.map((p, i) => {
                        const on = sel?.kind === 'placement' && sel.index === i;
                        return (
                          <li key={`p${i}`} className={on ? 'on' : ''}>
                            <button type="button" className="dx-placed-pick" onClick={() => pickPlaced({ kind: 'placement', index: i })}>
                              <span className="dx-placed-name">{labelOf(p.fieldKey)}</span>
                              {p.format === 'signedAt' && <span className="fs-pill neutral">signed at</span>}
                              <span className="fb-ol-tag page">p{p.page}</span>
                            </button>
                            <button type="button" className="fs-btn text sm" disabled={smartOff}
                              aria-label={`Remove ${labelOf(p.fieldKey)} from page ${p.page}`}
                              onClick={() => { setPlacements((l) => l.filter((_, j) => j !== i)); if (on) setSel(null); }}>Remove</button>
                          </li>
                        );
                      })}
                      {regions.map((r, i) => {
                        const on = sel?.kind === 'region' && sel.index === i;
                        const sc = scopes.find((s) => s.scope === r.scope);
                        const blocks = r.layout === 'blocks';
                        return (
                          <li key={`r${i}`} className={on ? 'on' : ''}>
                            <button type="button" className="dx-placed-pick" onClick={() => pickPlaced({ kind: 'region', index: i })}>
                              <span className="dx-placed-name">{sc?.label || r.scope} · {r.columns.length} {blocks ? 'answer' : 'column'}{r.columns.length === 1 ? '' : 's'}</span>
                              <span className="fs-pill neutral">{blocks ? 'block' : 'table'}</span>
                              <span className="fb-ol-tag page">p{r.page}</span>
                            </button>
                            <button type="button" className="fs-btn text sm" disabled={smartOff}
                              aria-label={`Remove the ${sc?.label || r.scope} ${blocks ? 'block' : 'table'} from page ${r.page}`}
                              onClick={() => { setRegions((l) => l.filter((_, j) => j !== i)); if (on) setSel(null); }}>Remove</button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* ---- the selected box's or table's properties ---- */}
                  {sel && (
                    <div className="dx-props">
                      <SectionLabel className="dx-label">{sel.kind === 'region' ? (regions[sel.index]?.layout === 'blocks' ? 'This block' : 'This table') : 'This box'}</SectionLabel>
                      <PlacementProps
                        sel={sel} placements={placements} regions={regions} onChange={setBoxes} onSel={setSel}
                        labelOf={labelOf} scopes={scopes} signatureKeys={signatureKeys}
                        answerGroups={answerGroups} scopeGroups={scopeGroups} disabled={smartOff}
                        pageHeight={template.height}
                      />
                    </div>
                  )}

                  {/*
                    A PDF prepared in Acrobat names its fields; a flat design
                    (which is most of them, including every one exported from a
                    design tool) does not. The two need different things from
                    the author, so the difference is said plainly rather than
                    shown as an empty table.
                  */}
                  {!!template.acroFields?.length && (
                    <div className="fs-field">
                      <SectionLabel className="dx-label">Which answer fills which field</SectionLabel>
                      <div className="hint" style={{ marginBottom: 8 }}>
                        This PDF names its fields, so answers can be dropped straight into them.
                        The matches below were guessed from the names — check them.
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {template.acroFields.map((name) => (
                          <div key={name} className="dx-acro-row">
                            <code style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</code>
                            <FieldPicker
                              value={acroMap[name] || ''} disabled={smartOff} groups={acroGroups}
                              ariaLabel={`Answer for ${name}`} placeholder="— leave as the template has it —"
                              onChange={(v) => setAcroMap((m) => {
                                const next = { ...m };
                                if (v) next[name] = v; else delete next[name];
                                return next;
                              })}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="hint" style={{ marginTop: 8 }}>
                        Unmapped fields are left exactly as the template has them — blanking somebody&rsquo;s
                        own paperwork is not this feature&rsquo;s job.
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="fs-field">
                  <label htmlFor="fd-template">Template PDF</label>
                  <input
                    id="fd-template" type="file" accept="application/pdf,.pdf"
                    disabled={uploading || smartOff}
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void onTemplate(f); }}
                  />
                  {smartOff ? <PaidLine /> : (
                    <>
                      <div className="hint">
                        Upload the paperwork you already use and answers land on it. Up to 20 MB, checked as it
                        uploads — a password-protected PDF is refused rather than failing later. Without one, the
                        document is built from the parts below alone.
                      </div>
                      <div className="fd-add-pdf">
                        <button type="button" className="fs-btn neutral sm" disabled={uploading} onClick={() => void onAddBlank()}>
                          + Start from a blank page
                        </button>
                        <span className="fs-cell-sub">and place the answers exactly where you want them</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </section>

            {/* ---- 2 · The pages, in order ------------------------------- */}
            <section className="dx-sec">
              <SectionLabel className="dx-label">The pages, in order</SectionLabel>
              <div className="hint">
                What the finished PDF contains. Template pages are one kind of part; the rest are built
                from the answers. With nothing here, the form sends the <b>branded record</b> — every
                question and answer, with your logo.
              </div>
              {blocks.length ? (
                <div className="fd-parts">
                  {blocks.map((b, i) => {
                    const t = b.type === 'template' ? templateForPart(templates, b) : undefined;
                    const onePage = b.type === 'template' && b.pages?.length === 1 ? b.pages[0] : null;
                    // Its page, or the first of its pages.
                    const shown = onePage ?? (b.type === 'template' ? (b.pages?.[0] ?? 1) : 1);
                    const thumb = t && !t.blank ? thumbs[t.url]?.[shown - 1] : undefined;
                    const splittable = !!t && (b.type === 'template') && (b.pages?.length ? b.pages.length > 1 : t.pages > 1);
                    return (
                      <div
                        key={i}
                        className={`fd-part${dragFrom === i ? ' is-dragging' : ''}${dropAt === i && dragFrom !== null ? ' drop-before' : ''}`}
                        draggable
                        onDragStart={(e) => { setDragFrom(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                        onDragOver={(e) => {
                          if (dragFrom === null) return;
                          e.preventDefault();
                          const r = e.currentTarget.getBoundingClientRect();
                          // The top half of a row drops before it, the bottom half after.
                          setDropAt(e.clientY - r.top < r.height / 2 ? i : i + 1);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragFrom !== null && dropAt !== null) moveTo(dragFrom, dropAt);
                          setDragFrom(null); setDropAt(null);
                        }}
                        onDragEnd={() => { setDragFrom(null); setDropAt(null); }}
                      >
                        <span className="no" title="Drag to move">{i + 1}</span>
                        <span className={`what${t ? ' has-thumb' : ''}`}>
                          {t && (
                            <span className={`fd-part-thumb${t.blank ? ' blank' : ''}`} aria-hidden="true">
                              {thumb ? <img src={thumb} alt="" /> : null}
                            </span>
                          )}
                          <span className="what-text">
                            <span className="kind">{KIND_LABEL[b.type]}</span>
                            <span className="fs-cell-sub">{blockSummary(b, ctx)}</span>
                          </span>
                        </span>
                        <span className={`fs-pill ${b.type === 'template' ? 'identity' : 'neutral'}`}>
                          {b.type !== 'template' ? 'built' : t?.blank ? 'blank page' : 'your PDF'}
                        </span>
                        <RowMenu open={openMenu === i} onToggle={(o) => setOpenMenu(o ? i : null)}>
                          <button type="button" onClick={() => setEditingPart(i)}>Edit…</button>
                          {splittable && (
                            <button type="button" onClick={() => { setOpenMenu(null); splitPart(i); }}>Split into pages</button>
                          )}
                          <div className="rule" />
                          <button type="button" disabled={i === 0} onClick={() => move(i, -1)}>Move up</button>
                          <button type="button" disabled={i === blocks.length - 1} onClick={() => move(i, 1)}>
                            Move down
                          </button>
                          {!smartOff && (
                            <button type="button" disabled={uploading}
                              onClick={() => { setOpenMenu(null); void onAddBlank(i + 1); }}>
                              Add a blank page below
                            </button>
                          )}
                          <div className="rule" />
                          <button type="button" className="danger"
                            onClick={() => setBlocks((list) => list.filter((_, j) => j !== i))}>
                            Remove
                          </button>
                        </RowMenu>
                      </div>
                    );
                  })}
                  {dragFrom !== null && (
                    // The end of the list, as a place to drop.
                    <div
                      className={`fd-part-end${dropAt === blocks.length ? ' drop-before' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setDropAt(blocks.length); }}
                      onDrop={(e) => { e.preventDefault(); moveTo(dragFrom, blocks.length); setDragFrom(null); setDropAt(null); }}
                    />
                  )}
                </div>
              ) : (
                <div className="fs-cell-sub">
                  {multi
                    ? 'Your PDFs, filled in, one after another in the order above. Add parts to choose their pages and put built pages between them.'
                    : template
                      ? 'Your PDF, filled in, exactly as uploaded. Add parts to put built pages before or after it.'
                      : 'Nothing arranged — the branded record is sent. Add a part to build something else.'}
                </div>
              )}

              <div className="fd-foot">
                <span className="fs-menu-anchor">
                  <button type="button" className="fs-btn neutral sm" onClick={() => setAdding(!adding)}>
                    + Add a part ▾
                  </button>
                  {adding && (
                    <div className="fs-menu fd-add-menu">
                      {palette(ctx, !smartOff).map((p) => (
                        <button
                          key={p.kind} type="button" disabled={p.disabled} title={p.hint}
                          onClick={() => addPart(p.kind)}
                        >
                          {KIND_LABEL[p.kind]}
                          {p.hint && <small style={{ display: 'block', color: 'var(--text-faint)' }}>{p.hint}</small>}
                        </button>
                      ))}
                      <button
                        type="button" disabled={smartOff || uploading}
                        title={smartOff ? 'Your own PDF is on every paid plan.' : undefined}
                        onClick={() => {
                          setAdding(false);
                          // Where it lands: after what is there — on a document
                          // with nothing arranged, after its PDFs' pages.
                          void onAddBlank(blocks.length ? blocks.length : undefined);
                        }}
                      >
                        Blank page
                        <small style={{ display: 'block', color: 'var(--text-faint)' }}>
                          {smartOff ? 'Your own PDF is on every paid plan.' : 'An empty page to place answers on'}
                        </small>
                      </button>
                    </div>
                  )}
                </span>
                {template && (
                  <button type="button" className="fs-btn neutral sm" onClick={() => { setView('preview'); void loadPreview(); }}>
                    Preview with sample answers
                  </button>
                )}
                {unusedTemplatePage && (
                  <span className="fs-cell-sub spare">
                    {unusedTemplatePage.page === null
                      ? <>{templateName(unusedTemplatePage.t)} is not used</>
                      : multi
                        ? <>Page {unusedTemplatePage.page} of {templateName(unusedTemplatePage.t)} is not used</>
                        : <>Template page {unusedTemplatePage.page} is not used</>} —{' '}
                    <button
                      type="button" className="fd-linkish"
                      onClick={() => {
                        const { t, page: p } = unusedTemplatePage;
                        setBlocks((list) => [...list, { type: 'template', templateId: t.id, ...(p === null ? {} : { pages: [p] }) }]);
                        setEditingPart(blocks.length);
                      }}
                    >
                      add it
                    </button>
                  </span>
                )}
              </div>
            </section>

            {/* ---- 3 · File name and download link ----------------------- */}
            <section className="dx-sec">
              <SectionLabel className="dx-label">File name and download link</SectionLabel>
              <div className="fs-field">
                <label htmlFor="fd-filename">File name</label>
                <input
                  ref={filenameRef} id="fd-filename" placeholder="{{formName}}-{{submissionId}}"
                  value={filename} onChange={(e) => setFilename(e.target.value)}
                />
                <TokenPicker groups={filenameGroups} onPick={insertIntoFilename} label="Insert a form value into File name" />
                <div className="hint">
                  What it is called in an inbox or a downloads folder. <code>{'{{fieldKey}}'}</code> tokens
                  allowed — <code>{'{{companyName}}-quote'}</code> reads better than a submission id.{' '}
                  <code>.pdf</code> is added for you.
                </div>
              </div>
              <div className="fs-field">
                <label htmlFor="fd-ttl">Download link lives for</label>
                <select id="fd-ttl" value={ttl} onChange={(e) => setTtl(e.target.value)}>
                  {TTL_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <div className="hint">
                  How long a thank-you page or email link keeps working. Downloads from Submissions never
                  expire. Applies to links made from now on.
                </div>
              </div>
            </section>

            {/* ---- Where it goes: a read-only summary; the workflow decides ---- */}
            <section className="dx-sec">
              <SectionLabel className="dx-label">Where it goes</SectionLabel>
              <div className="fs-cell-sub dx-uses-line">
                <UseCount uses={uses} />
              </div>
              {uses.length > 0 && (
                <div className="dx-uses">
                  {uses.map((u) => (
                    <div className="fd-use" key={u.nodeId}>
                      <span className={`fs-pill ${u.kind === 'step' ? 'info' : 'neutral'}`}>{u.kind}</span>
                      <span className="where">
                        <Link to={`../workflow?node=${encodeURIComponent(u.nodeId)}`}>{u.title}</Link>
                        <span className="fs-cell-sub">{u.detail}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="fs-cell-sub" style={{ marginTop: 6 }}>
                Steps decide <i>whether</i> to send it; this page decides <i>what</i> it is. Always available
                from <Link to="../submissions">Submissions</Link> — every row&rsquo;s ⋯ has Download PDF.
              </div>
            </section>

            <div className="fs-cell-sub dx-savenote">
              Saved into the draft with Fields and Logic — it goes live when you publish.{' '}
              <b>Changes apply to submissions from then on; documents already sent are kept exactly as sent.</b>
            </div>
          </div>

          <SaveBar>
            <button className="fs-btn primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save document'}
            </button>
            <span className="note">
              {dirty ? 'Unsaved changes on this screen.' : 'Saved in the draft — publish to make it live.'}
            </span>
          </SaveBar>
        </aside>
      </div>

      {editingPart !== null && blocks[editingPart] && (
        <DocBlockEditor
          block={blocks[editingPart]}
          index={editingPart + 1}
          ctx={ctx}
          onCancel={() => setEditingPart(null)}
          onSave={(next) => {
            setBlocks((list) => list.map((b, i) => (i === editingPart ? next : b)));
            setEditingPart(null);
          }}
        />
      )}

      {replacing && (
        <Modal
          narrow
          title="Replace the PDF?"
          onClose={() => setReplacing(null)}
          footer={(
            <>
              <button
                type="button" className="fs-btn danger"
                onClick={() => { const r = replacing; setReplacing(null); void onTemplate(r.file, r.id); }}
              >
                Replace and clear
              </button>
              <button type="button" className="fs-btn neutral" onClick={() => setReplacing(null)}>Cancel</button>
            </>
          )}
        >
          {(() => {
            const mine = edits[replacing.id] || NO_EDIT;
            const t = templates.find((x) => x.id === replacing.id);
            return (
              <div className="fs-consequence">
                Replacing {multi && t ? templateName(t) : 'the PDF'} clears {mine.placements.length} position{mine.placements.length === 1 ? '' : 's'}
                {mine.regions.length ? ` and ${mine.regions.length} table${mine.regions.length === 1 ? '' : 's'}` : ''} — they
                belong to pages this file will not have.
              </div>
            );
          })()}
          <div className="fs-cell-sub" style={{ marginTop: 12 }}>
            The mapping goes too: field names belong to the document that carries them. Everything else on
            this screen — the parts, the file name, the link lifetime — is untouched.
          </div>
        </Modal>
      )}

      {shrinking && (
        <Modal
          narrow
          title="Remove pages?"
          onClose={() => setShrinking(null)}
          footer={(
            <>
              <button
                type="button" className="fs-btn danger"
                onClick={() => {
                  const s = shrinking;
                  setShrinking(null);
                  const t = templates.find((x) => x.id === s.id);
                  if (t) onBlankPages(t, s.pages, true);
                }}
              >
                Remove and clear
              </button>
              <button type="button" className="fs-btn neutral" onClick={() => setShrinking(null)}>Cancel</button>
            </>
          )}
        >
          <div className="fs-consequence">
            Going down to {shrinking.pages} page{shrinking.pages === 1 ? '' : 's'} clears {shrinking.lost} answer
            {shrinking.lost === 1 ? '' : 's'} placed on the pages that go.
          </div>
        </Modal>
      )}

      {guard.node}
    </form>
  );
}

/** "attached on 2 steps · offered on 1 ending", or the empty reading. */
function UseCount({ uses }: { uses: DocumentUse[] }) {
  const steps = uses.filter((u) => u.kind === 'step').length;
  const endings = uses.filter((u) => u.kind === 'ending').length;
  if (!steps && !endings) {
    return (
      <>
        <b>Not sent anywhere yet</b> — attach it on a step or offer it on an ending{' '}
        <Link to="../workflow">Open workflow →</Link>
      </>
    );
  }
  const parts = [
    steps ? `attached on ${steps} step${steps === 1 ? '' : 's'}` : '',
    endings ? `offered on ${endings} ending${endings === 1 ? '' : 's'}` : '',
  ].filter(Boolean).join(' · ');
  return <><b>Sent by the workflow: {parts}</b> · <Link to="../workflow">Open workflow →</Link></>;
}

/** What a heading or paragraph in a part can insert — the answers arranged as the form is built. */
function tokenGroupsFor(
  answerGroups: OptGroup[], scopes: ScopeOption[]
): FieldGroup[] {
  return [
    ...answerGroups.map((g) => ({ label: g.label, options: g.opts.map((o) => ({ key: o.v, label: o.l })) })),
    {
      label: 'Facts',
      options: [
        { key: 'submissionId', label: 'Submission id' },
        { key: 'formName', label: 'Form name' },
        { key: 'submittedAt', label: 'Date submitted' },
      ],
    },
    ...scopes.map((sc) => ({
      label: `Each ${sc.label.toLowerCase()}`,
      options: [
        { key: sc.scope, label: `Repeat for each ${sc.label.toLowerCase()}`, insert: `{{#each ${sc.scope}}}\n\n{{/each}}` },
        ...sc.fields.map((f) => ({ key: f.key, label: f.label || f.key })),
      ],
    })),
  ];
}

/**
 * What the file name can be built from — exactly what `documentFilename` on
 * the server fills: the main form's answers, the submission id, the form's
 * name and its address. Not the date submitted, which the server does not
 * give it, and not an upload or a signature, whose answer is a stored file's
 * address and has no business in a file name.
 */
export function filenameTokenGroups(answerGroups: OptGroup[], fields: FieldDefinition[]): FieldGroup[] {
  const notAName = new Set(fields.filter((f) => f.input_type === 'file' || f.input_type === 'signature').map((f) => f.key));
  return [
    ...answerGroups.map((g) => ({
      label: g.label,
      options: g.opts.filter((o) => !notAName.has(o.v)).map((o) => ({ key: o.v, label: o.l })),
    })),
    {
      label: 'Facts',
      options: [
        { key: 'submissionId', label: 'Submission id' },
        { key: 'formName', label: 'Form name' },
        { key: 'formSlug', label: 'Form address (slug)' },
      ],
    },
  ];
}

export interface DocumentUse {
  nodeId: string;
  kind: 'step' | 'ending';
  title: string;
  detail: string;
}

/**
 * Every place on the workflow that sends this document.
 *
 * Read from the DRAWING rather than from the connection list, because the
 * drawing is what runs: an email connection with `attachDocument` set that no
 * node points at sends nothing, and listing it here would be an answer to a
 * question nobody asked ("what is configured") in place of the one on screen
 * ("where does this go").
 */
export function documentUses(
  graph: WorkflowGraph | null,
  connections: ConnectionRow[],
  fields: FieldDefinition[] = []
): DocumentUse[] {
  if (!graph) return [];
  const connById = new Map(connections.map((c) => [c.id, c]));
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const out: DocumentUse[] = [];

  for (const node of graph.nodes) {
    if (node.kind === 'deliver') {
      const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
      if (conn?.type !== 'email' || !conn.config?.attachDocument) continue;
      out.push({
        nodeId: node.id,
        kind: 'step',
        title: conn.name || 'Email step',
        detail: `attached · ${pathCondition(graph, byId, node, fields)}`,
      });
      continue;
    }
    if (node.kind === 'end' && node.config?.allowDocumentDownload) {
      const cfg = node.config;
      const title = cfg.mode === 'redirect'
        ? String(cfg.redirectUrl || 'Redirect').replace(/^https?:\/\//, '')
        : cfg.mode === 'continue' ? 'Into the follow-up form'
          : cfg.title ? `“${cfg.title}”` : 'Thank you';
      out.push({ nodeId: node.id, kind: 'ending', title, detail: 'download offered' });
    }
  }
  return out;
}

/**
 * "sent on every path", or the branch that decides it.
 *
 * Walks up the single-input spine to the start. A `no` arm is said as a
 * negation rather than dropped: "only when the total is under $5,000" is the
 * fact, and a row that said "only on a branch" would send somebody to the
 * canvas to find out which.
 */
function pathCondition(
  graph: WorkflowGraph,
  byId: Map<string, WorkflowNode>,
  node: WorkflowNode,
  fields: FieldDefinition[]
): string {
  const said: string[] = [];
  const seen = new Set<string>([node.id]);
  let current = node.id;
  for (let hops = 0; hops < 50; hops += 1) {
    const edge = graph.edges.find((e) => e.to === current);
    if (!edge) break;
    const from = byId.get(edge.from);
    if (!from || seen.has(from.id)) break;
    seen.add(from.id);
    if (from.kind === 'branch') {
      const group = (from.config?.when || null) as Parameters<typeof describeGroup>[0] | null;
      const words = group ? describeGroup(group, fields) : '';
      if (words) said.push(edge.port === 'no' ? `not when ${words}` : `only when ${words}`);
      else said.push(`on the ${edge.port === 'no' ? 'second' : 'first'} path of “${String(from.config?.name || 'a branch')}”`);
    }
    current = from.id;
  }
  return said.length ? said.join(' · ') : 'sent on every path';
}
